import {
	keyMetricsInfoSelector,
	signalCountsOnSpeedRangesSelector,
	summaryKeyMetricsInfoSelector,
	vesselCohortPhasePercentagesOfPolygonCallsSelector,
	vesselCohortSignalCountsOnSpeedRangesSelector,
	vesselCohortVoyagesSelector,
	vesselPolygonCallsSelector,
} from '../../kpiSelectors';
import { useAppDispatch, useAppSelector } from '../../../../core/rootHooks';
import { AppDispatch, RootState } from '../../../../core/store';
import { kpisActions } from '../../kpiSlice';
import { useEffect } from 'react';
import CohortSourceSelect from '../../../common/cohort/components/CohortSourceSelect';
import { selectCohortSource, setCohortSource } from '../../../common/cohort/cohortSourceSlice';
import CohortCharacteristics from '../../../common/cohort/containers/characteristics/CohortCharacteristics';
import { TVoyages } from '../../kpiTypes';
import { ICohortVessel } from '../../../common/cohort/cohortTypes';
import DynamicTab from '../../../../shared/components/tabs/DynamicTab';
import PortActivity from './portActivity/PortActivity';
import Speed from './speed/Speed';
import Deployment from './deployment/Deployment';
import CohortKeyMetrics from './cohortKeyMetrics/CohortKeyMetrics';
import { greenActions, selectCohortVesselKeyMetricsDateBased, selectVesselKeyMetricsDateBased } from '../../../green/greenSlice';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../date-range/slices/dateRangeSlice';

export interface IKpiCohortBenchmarkingProps {
	vessel: ICohortVessel;
	cohortVessels: ICohortVessel[] | [];
	minSize: number;
	maxSize: number;
	rangeSize: number;
	vesselVoyages: TVoyages | null;
	minAge: number;
	maxAge: number;
}

const KpiCohortBenchmarking: React.FC<IKpiCohortBenchmarkingProps> = ({ ...props }) => {
	const dispatch = useAppDispatch<AppDispatch>();
	const cohortSource = useAppSelector(selectCohortSource);

	const vesselKeyMetrics = useAppSelector((state: RootState) => keyMetricsInfoSelector(state, props.vessel.imoNumber!));
	const cohortKeyMetrics = useAppSelector(summaryKeyMetricsInfoSelector);
	const vesselCohortVoyages = useAppSelector((state: RootState) => vesselCohortVoyagesSelector(state, props.vessel.imoNumber!));
	const vesselSignalCountsOnSpeedRanges = useAppSelector((state: RootState) =>
		signalCountsOnSpeedRangesSelector(state, props.vessel.imoNumber!),
	);
	const vesselCohortSignalCountsOnSpeedRanges = useAppSelector((state: RootState) =>
		vesselCohortSignalCountsOnSpeedRangesSelector(state, props.vessel.imoNumber!),
	);
	const vesselPolygonCalls = useAppSelector((state: RootState) => vesselPolygonCallsSelector(state, props.vessel.imoNumber!));
	const vesselCohortPhasePercentagesOfPolygonCall = useAppSelector((state: RootState) =>
		vesselCohortPhasePercentagesOfPolygonCallsSelector(state, props.vessel.imoNumber!),
	);

	const vesselKeyMetricsDateBased = useAppSelector(selectVesselKeyMetricsDateBased);
	const cohortVesselKeyMetricsDateBased = useAppSelector(selectCohortVesselKeyMetricsDateBased);

	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);

	useEffect(() => {
		const startYearMonth = analysisDateRange.yearMonthRange?.start;
		const endYearMonth = analysisDateRange.yearMonthRange?.end;
		if (!startYearMonth || !endYearMonth) return;

		if (props.vessel.imoNumber) {
			dispatch(kpisActions.getVesselKeyMetricsRequestAction({ imoNumber: props.vessel.imoNumber, startYearMonth, endYearMonth }));
		}

		dispatch(
			greenActions.getVesselKeyMetricsDateBasedRequestAction({
				imoNumbers: [props.vessel.imoNumber],
				startYearMonth,
				endYearMonth,
			}),
		);
	}, [dispatch, props.vessel.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.vessel.imoNumber && props.cohortVessels.length != 0) {
			const imoList = props.cohortVessels.map((v) => v.imoNumber);
			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();
			dispatch(
				kpisActions.getVesselCohortVoyagesRequestAction({
					imoList: imoList,
					imoNumber: props.vessel.imoNumber,
					startDate,
					endDate,
				}),
			);
		}
	}, [dispatch, props.cohortVessels, props.vessel.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.cohortVessels.length != 0) {
			const imoList = props.cohortVessels.map((v) => v.imoNumber);

			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;
			if (!startYearMonth || !endYearMonth) return;

			dispatch(kpisActions.getSummaryKeyMetricsRequestAction({ imoList, startYearMonth, endYearMonth }));
			dispatch(kpisActions.getSummaryPhaseCountsOfGeneralSeaAreasRequestAction({ imoList, startYearMonth, endYearMonth }));

			dispatch(
				greenActions.getCohortVesselKeyMetricsDateBasedRequestAction({ imoNumbers: imoList, startYearMonth, endYearMonth }),
			);
		}
	}, [dispatch, props.cohortVessels, props.vessel.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.vessel.imoNumber) {
			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;
			if (!startYearMonth || !endYearMonth) return;
			dispatch(
				kpisActions.getSignalCountsOnSpeedRangesRequestAction({
					imoNumber: props.vessel.imoNumber,
					startYearMonth,
					endYearMonth,
				}),
			);
		}
	}, [dispatch, props.vessel.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.vessel.imoNumber && props.cohortVessels.length != 0) {
			const imoList = props.cohortVessels.map((v) => v.imoNumber);

			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;
			if (!startYearMonth || !endYearMonth) return;

			dispatch(
				kpisActions.getVesselCohortSignalCountsOnSpeedRangesRequestAction({
					imoList: imoList,
					imoNumber: props.vessel.imoNumber,
					startYearMonth,
					endYearMonth,
				}),
			);
		}
	}, [dispatch, props.cohortVessels, props.vessel.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.vessel.imoNumber) {
			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();
			dispatch(kpisActions.getVesselPolygonCallsRequestAction({ imoNumber: props.vessel.imoNumber, startDate, endDate }));
		}
	}, [dispatch, props.vessel.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.vessel.imoNumber && props.cohortVessels.length != 0) {
			const imoList = props.cohortVessels.map((v) => v.imoNumber);
			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;
			if (!startYearMonth || !endYearMonth) return;

			dispatch(
				kpisActions.getVesselCohortPhasePercentagesOfPolygonCallsRequestAction({
					imoList: imoList,
					imoNumber: props.vessel.imoNumber,
					startYearMonth,
					endYearMonth,
				}),
			);
		}
	}, [dispatch, props.cohortVessels, props.vessel.imoNumber, analysisDateRange]);

	const tabs = [
		{
			id: 'characteristics',
			label: 'Characteristics',
			content: (
				<CohortCharacteristics
					vessel={props.vessel}
					cohortVessels={props.cohortVessels}
					minSize={props.minSize}
					maxSize={props.maxSize}
					rangeSize={props.rangeSize}
					minAge={props.minAge}
					maxAge={props.maxAge}
				/>
			),
		},
		{
			id: 'keyMetrics',
			label: 'Key Metrics',
			content: (
				<CohortKeyMetrics
					vesselKeyMetrics={vesselKeyMetrics}
					cohortKeyMetrics={cohortKeyMetrics}
					vessel={props.vessel}
					minSize={props.minSize}
					maxSize={props.maxSize}
					minAge={props.minAge}
					maxAge={props.maxAge}
					cohortVessels={props.cohortVessels}
					vesselKeyMetricsDateBased={vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData}
					vesselCohortKeyMetricsDateBased={cohortVesselKeyMetricsDateBased.cohortVesselKeyMetricsDateBasedData}
				/>
			),
		},
		{
			id: 'deployment',
			label: 'Deployment',
			content: (
				<Deployment
					vessel={props.vessel}
					minSize={props.minSize}
					maxSize={props.maxSize}
					minAge={props.minAge}
					maxAge={props.maxAge}
					cohortVessels={props.cohortVessels}
					vesselVoyages={props.vesselVoyages}
					cohortVesselVoyages={vesselCohortVoyages}
				/>
			),
		},
		{
			id: 'speed',
			label: 'Speed',
			content: (
				<Speed
					vessel={props.vessel}
					minSize={props.minSize}
					maxSize={props.maxSize}
					minAge={props.minAge}
					maxAge={props.maxAge}
					cohortVessels={props.cohortVessels}
					vesselSignalCountsOnSpeedRanges={vesselSignalCountsOnSpeedRanges}
					vesselCohortSignalCountsOnSpeedRanges={vesselCohortSignalCountsOnSpeedRanges}
				/>
			),
		},
		{
			id: 'portActivity',
			label: 'Port Activity',
			content: (
				<PortActivity
					vessel={props.vessel}
					cohortVessels={props.cohortVessels}
					maxSize={props.maxSize}
					minSize={props.minSize}
					minAge={props.minAge}
					maxAge={props.maxAge}
					vesselCohortPhasePercentagesOfPolygonCall={vesselCohortPhasePercentagesOfPolygonCall}
					vesselPolygonCalls={vesselPolygonCalls}
				/>
			),
		},
	];

	return (
		<div className="flex flex-col w-full h-full">
			<CohortSourceSelect selected={cohortSource} onChange={(selection) => dispatch(setCohortSource(selection))} />
			<DynamicTab
				items={tabs}
				key={props.vessel.imoNumber}
				tabColor="msi-darkgrey-color"
				props={{ isVertical: true }}
				giveSpace={false}
				isSubtab
			/>
		</div>
	);
};

export default KpiCohortBenchmarking;
